import React from 'react';
import {Container} from 'react-bootstrap';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faHandshake, faHeadset, faScaleBalanced, faCoins,
    faRobot, faChartColumn, faMobileScreen, faBrain, faBuildingColumns,
    faArrowDown
} from '@fortawesome/free-solid-svg-icons';

const consumers = [
    {icon: faRobot,          label: 'Agents',         desc: 'Agents, copilots, workflow automation'},
    {icon: faChartColumn,    label: 'BI & Reporting', desc: 'Dashboards, regulatory reports'},
    {icon: faMobileScreen,   label: 'Apps',           desc: 'Internal tools, customer-facing products'},
    {icon: faBrain,          label: 'Data Science',   desc: 'Models, forecasts, what-if analysis'},
    {icon: faBuildingColumns,label: 'Board & Exec',   desc: 'Strategic questions, audit responses'}
];

const departments = [
    {
        icon: faHandshake,
        accentClass: 'accent-secondary',
        name: 'Sales & Marketing',
        scope: 'Lead · customer · opportunity · quote',
        knowledge: [
            'Product catalogue & pricing rules',
            'Eligibility & qualification policy',
            'Customer relationships & history'
        ]
    },
    {
        icon: faHeadset,
        accentClass: 'accent-primary',
        name: 'Customer Service',
        scope: 'Case · ticket · service level',
        knowledge: [
            'Service playbooks & escalation paths',
            'Product knowledge base',
            'Customer interaction history'
        ]
    },
    {
        icon: faScaleBalanced,
        accentClass: 'accent-primary-dark',
        name: 'Legal & Compliance',
        scope: 'Regulation · obligation · override',
        knowledge: [
            'FCA / MHRA / GDPR rule sets',
            'Internal compliance policy',
            'Regulatory amendments & advisories'
        ]
    },
    {
        icon: faCoins,
        accentClass: 'accent-accent',
        name: 'Finance',
        scope: 'Transaction · invoice · control',
        knowledge: [
            'Chart of accounts & controls',
            'Audit and reporting standards',
            'Approval & reconciliation rules'
        ]
    }
];

const FinalStateSection = () => (
    <section className="finalstate-section">
        <Container>
            <div className="text-center mb-5">
                <div className="section-label">The Final State</div>
                <h2>Trusted AI-driven answers across your business.</h2>
                <p className="section-intro">
                    Every part of your business has its own knowledge.
                    Every tool you already use — agents, dashboards, apps, models,
                    or a board-level question — gets the same cited, consistent answer
                    from the right place at the right moment.
                </p>
            </div>

            <div className="finalstate-architecture">

                {/* Consumer layer — what asks the questions */}
                <div className="finalstate-consumers">
                    <div className="finalstate-layer-label">WHAT ASKS THE QUESTIONS</div>
                    <div className="finalstate-consumer-row">
                        {consumers.map((c, i) => (
                            <div key={i} className="finalstate-consumer">
                                <div className="finalstate-consumer-icon">
                                    <FontAwesomeIcon icon={c.icon}/>
                                </div>
                                <div className="finalstate-consumer-label">{c.label}</div>
                                <div className="finalstate-consumer-desc">{c.desc}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Trust layer — the band that sits between consumers and knowledge */}
                <div className="finalstate-trust-band">
                    <div className="finalstate-trust-band-inner">
                        <div className="finalstate-trust-band-label">THE TRUST LAYER</div>
                        <div className="finalstate-trust-band-title">
                            Every answer cited · consistent · auditable
                        </div>
                        <div className="finalstate-trust-band-desc">
                            Routes each question to the right domain. Resolves entities at
                            the seams. Captures every override. Learns from every decision.
                        </div>
                    </div>
                </div>

                {/* Connectors down to departments */}
                <div className="finalstate-connectors" aria-hidden="true">
                    {departments.map((_, i) => (
                        <div key={i} className="finalstate-connector">
                            <div className="finalstate-connector-line"/>
                            <FontAwesomeIcon icon={faArrowDown} className="finalstate-connector-arrow"/>
                        </div>
                    ))}
                </div>

                {/* Department knowledge bases */}
                <div className="finalstate-layer-label finalstate-layer-label-center">
                    YOUR SCOPED KNOWLEDGE — ONE PER DOMAIN
                </div>
                <div className="finalstate-departments">
                    {departments.map((d, i) => (
                        <div key={i} className={`finalstate-dept ${d.accentClass}`}>
                            <div className="finalstate-dept-header">
                                <div className="finalstate-dept-icon">
                                    <FontAwesomeIcon icon={d.icon}/>
                                </div>
                                <div>
                                    <div className="finalstate-dept-name">{d.name}</div>
                                    <div className="finalstate-dept-scope">{d.scope}</div>
                                </div>
                            </div>
                            <div className="finalstate-dept-kb-label">KNOWLEDGE BASE</div>
                            <ul className="finalstate-dept-list">
                                {d.knowledge.map((k, ki) => (
                                    <li key={ki}>{k}</li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                {/* Seam strip */}
                <div className="finalstate-seams">
                    <span>
                        Customer in Sales is the same person as customer in Finance —
                        with the meaning that matters in each context. The trust layer
                        resolves the seams so nothing falls between domains.
                    </span>
                </div>
            </div>
        </Container>
    </section>
);

export default FinalStateSection;